import { MoveDownRight, MoveUpRight, Plus } from 'lucide-react';
import { formatAbsoluteGrowth } from 'utils/helpers';
import { Badge } from '@/ui/design-system/components/ui/badge';
import { Button } from '@/ui/design-system/components/ui/button';
import HashtagBadge from './HashtagBadge';
import { useState } from 'react';

function HashtagsList({
    hashtags,
    displayQty = 6,
    country,
    category,
    brand,
    metricLabel = 'Views',
    enableFlagging = true,
    showMoreItemLabel = 'hashtags',
    listColumns = 2,
    itemMaxWidthClass = 'max-w-[180px]',
    translationLanguage = null,
}) {
    const [numToDisplay, setNumToDisplay] = useState(displayQty);

    if (!hashtags || hashtags.length === 0) return null;

    const visibleHashtags = hashtags.slice(0, numToDisplay);
    const hasMore = hashtags.length > numToDisplay;

    return (
        <div>
            <div
                className={`grid ${listColumns === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-x-8`}
            >
                {visibleHashtags.map((hashtag, index) => {
                    const isPositive = (hashtag.volume ?? 0) >= 0;
                    const formattedValue = formatAbsoluteGrowth(hashtag.volume);
                    const translation =
                        hashtag.translations?.[translationLanguage];
                    return (
                        <div
                            key={hashtag.id || index}
                            className="flex items-end py-3"
                        >
                            <HashtagBadge
                                hashtag={translation || hashtag.kw}
                                translationLanguage={
                                    translation ? translationLanguage : null
                                }
                                flagId={hashtag.flagId}
                                enableFlagging={enableFlagging}
                                maxWidthClass={itemMaxWidthClass}
                                flagData={{
                                    hashtags: [hashtag.id],
                                    hashtag: hashtag.kw,
                                    ...(country && { countryId: country }),
                                    ...(category && { categoryId: category }),
                                    ...(brand && { brandId: brand }),
                                }}
                            />

                            <div className="flex-1 border-b border-dashed border-neutral-200 ml-[1ch] mr-[1ch]" />

                            <Badge
                                className={`shrink-0 border-transparent ${
                                    isPositive
                                        ? 'bg-green-100 text-foreground'
                                        : 'bg-red-50 text-foreground'
                                }`}
                            >
                                <span className="uppercase">
                                    {formattedValue}
                                </span>{' '}
                                {metricLabel}
                                {isPositive ? (
                                    <MoveUpRight className="size-3 text-green-600" />
                                ) : (
                                    <MoveDownRight className="size-3 text-red-600" />
                                )}
                            </Badge>
                        </div>
                    );
                })}
            </div>

            {(hasMore || numToDisplay > displayQty) && (
                <div className="flex justify-center gap-2 py-3">
                    {hasMore && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                                setNumToDisplay(numToDisplay + displayQty)
                            }
                            className="text-primary"
                        >
                            <Plus className="size-4" />
                            {`Show more ${showMoreItemLabel}`}
                        </Button>
                    )}
                    {numToDisplay > displayQty && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setNumToDisplay(displayQty)}
                            className="text-primary"
                        >
                            Show less
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

export default HashtagsList;
